"use client";
import * as app_organization_slug_agencies_agency_id_layout_context_ts from "@/app/(actor)/[organization-slug]/agencies/[agency-id]/layout.context";
import * as app_organization_slug_cases_case_id_layout_context_ts from "@/app/(actor)/[organization-slug]/cases/[case-id]/layout.context";
import * as app_organization_slug_host_families_host_family_id_layout_context_ts from "@/app/(actor)/[organization-slug]/host-families/[host-family-profile-id]/layout.context";
import * as app_organization_slug_layout_context_ts from "@/app/(actor)/[organization-slug]/layout.context";
import * as app_organization_slug_leads_lead_id_layout_context_ts from "@/app/(actor)/[organization-slug]/leads/[lead-id]/layout.context";
import * as app_organization_slug_organization_forms_form_id_layout_context_ts from "@/app/(actor)/[organization-slug]/organization/forms/[form-id]/layout.context";
import * as app_organization_slug_programs_program_id_layout_context_ts from "@/app/(actor)/[organization-slug]/organization/programs/[program-id]/layout.context";
import * as app_organization_slug_schools_school_id_layout_context_ts from "@/app/(actor)/[organization-slug]/schools/[school-id]/layout.context";
import * as app_organization_slug_students_student_profile_id_layout_context_ts from "@/app/(actor)/[organization-slug]/students/[student-profile-id]/layout.context";
import * as app_organization_slug_team_members_organization_member_id_layout_context_ts from "@/app/(actor)/[organization-slug]/team-members/[employee-profile-id]/layout.context";
import * as app_organization_slug_teams_team_id_layout_context_ts from "@/app/(actor)/[organization-slug]/teams/[team-id]/layout.context";
import * as app_agency_agencies_subagency_id_layout_context_ts from "@/app/(actor)/a/[agency-id]/agencies/[subagency-id]/layout.context";
import * as app_agency_id from "@/app/(actor)/a/[agency-id]/layout.context";
import * as app_agency_students_id from "@/app/(actor)/a/[agency-id]/students/[student-profile-id]/layout.context";
import * as app_actor_layout_context_ts from "@/app/(actor)/layout.context";
import mapObject, { mapObjectSkip } from "map-obj";
import path from "pathe";
import { use } from "react";
import type { Context } from "react";

const layouts = {
  "app/(actor)/layout.context.ts": app_actor_layout_context_ts,
  "app/a/[agency-id]/layout.context.ts": app_agency_id,
  "app/a/[agency-id]/students/[student-profile-id]/layout.context.ts": app_agency_students_id,
  "app/a/[agency-id]/agencies/[subagency-id]/layout.context.ts": app_agency_agencies_subagency_id_layout_context_ts,
  "app/[organization-slug]/layout.context.ts": app_organization_slug_layout_context_ts,
  "app/[organization-slug]/host-families/[host-family-id]/layout.context.ts": app_organization_slug_host_families_host_family_id_layout_context_ts,
  "app/[organization-slug]/students/[student-profile-id]/layout.context.ts": app_organization_slug_students_student_profile_id_layout_context_ts,
  "app/[organization-slug]/agencies/[agency-id]/layout.context.ts": app_organization_slug_agencies_agency_id_layout_context_ts,
  "app/[organization-slug]/schools/[school-id]/layout.context.ts": app_organization_slug_schools_school_id_layout_context_ts,
  "app/[organization-slug]/team-members/[employee-profile-id]/layout.context.ts": app_organization_slug_team_members_organization_member_id_layout_context_ts,
  "app/[organization-slug]/teams/[team-id]/layout.context.ts": app_organization_slug_teams_team_id_layout_context_ts,
  "app/[organization-slug]/cases/[case-id]/layout.context.ts": app_organization_slug_cases_case_id_layout_context_ts,
  "app/[organization-slug]/leads/[lead-id]/layout.context.ts": app_organization_slug_leads_lead_id_layout_context_ts,
  "app/[organization-slug]/organization/programs/[program-id]/layout.context.ts": app_organization_slug_programs_program_id_layout_context_ts,
  "app/[organization-slug]/organization/forms/[form-id]/layout.context.ts": app_organization_slug_organization_forms_form_id_layout_context_ts,
};

const layoutsWithContext = mapObject(layouts, (key, value) =>
  "default" in value
    ? [
      path.dirname(
        key.replace(
          `${path.relative(process.cwd(), path.join(process.cwd(), "app"))}/`,
          "",
        ),
      ),
      value,
    ]
    : mapObjectSkip) as unknown as {
    [
      K in keyof typeof layouts as (typeof layouts)[K] extends {
        default: any;
      } ? K extends `app/${infer $RelativeDirpath}/layout.context.ts` ? $RelativeDirpath
        : never
        : never
    ]: (typeof layouts)[K];
  };

export function app<
  $LayoutPath extends keyof typeof layoutsWithContext,
>(relativeDirpath: $LayoutPath): (typeof layoutsWithContext)[$LayoutPath]["default"] {
  const layout = layoutsWithContext[relativeDirpath];

  if (layout === undefined) {
    throw new Error(
      `Layout context not found for ${relativeDirpath}. Please ensure the layout context is defined.`,
    );
  }

  return layout.default;
}

export function useLayoutContext<
  $LayoutPath extends keyof typeof layoutsWithContext,
>(
  relativeDirpath: $LayoutPath,
): (typeof layoutsWithContext)[$LayoutPath]["default"] extends Context<infer T> ? T
  : never
{
  // @ts-expect-error: correct type
  const context = use(app(relativeDirpath));

  if (!context) {
    throw new Error("Layout context not found");
  }

  // @ts-expect-error: correct type
  return context;
}
